import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { t, type MessageKey } from '@aquabox/shared';
import { useMe, useRegionMutations, useRegions, useVehicles } from '../api/queries';
import { useShadowAssign, type ShadowRow, type ShadowVia } from '../api/shadow-assign';
import { useToast } from '../components/Toast';

// ── Etiketler ─────────────────────────────────────────────────────────────
// Türkçe metin tr.ts'ten gelir; burada yalnız anahtar eşlemesi var.

const VIA_KEYS: Record<ShadowVia | 'none', MessageKey> = {
  STREET_VEHICLE: 'panel.shadowAssign.via.STREET_VEHICLE',
  STREET_REGION: 'panel.shadowAssign.via.STREET_REGION',
  NEIGHBORHOOD: 'panel.shadowAssign.via.NEIGHBORHOOD',
  none: 'panel.shadowAssign.via.none',
};

/** Kuralın nereden geldiği (sokak aracı / sokak bölgesi / mahalle / yok). */
export function viaLabel(via: ShadowVia | null): string {
  return t(VIA_KEYS[via ?? 'none']);
}

/** Oran metni: `%66,7`; karşılaştırılamayan (null) için `—`. */
export function rateText(ratePct: number | null): string {
  if (ratePct === null) return t('panel.shadowAssign.dash');
  return `%${ratePct.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
}

export function sourceLabel(source: ShadowRow['source']): string {
  return t(
    source === 'HAYAT_PANEL' ? 'panel.shadowAssign.sourceHayat' : 'panel.shadowAssign.sourceAbant',
  );
}

/** "SS:dd" — Europe/Istanbul saati (CLAUDE.md kural 8). */
function timeText(iso: string): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

// ── Kapı ──────────────────────────────────────────────────────────────────
// Yalnız OWNER ve MANAGER görür; DRIVER için hiçbir istek atılmaz.

export function ShadowAssignCard() {
  const me = useMe();
  const canView = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  if (!canView) return null;
  return <ShadowAssignBody />;
}

function ShadowAssignBody() {
  const [days, setDays] = useState<7 | 30>(7);
  const shadow = useShadowAssign({ days });
  const regions = useRegions();
  const vehicles = useVehicles();
  const { update } = useRegionMutations();
  const qc = useQueryClient();
  const toast = useToast();

  const data = shadow.data;
  const regionItems = regions.data?.items ?? [];
  const vehicleItems = (vehicles.data?.items ?? []).filter((v) => v.isActive);
  const byRegionId = new Map(
    (data?.byRegion ?? []).flatMap((r) => (r.region ? [[r.region.id, r] as const] : [])),
  );
  const mismatches = (data?.rows ?? []).filter((r) => r.agree === false).slice(0, 50);
  const summary = data?.summary ?? null;
  // Bölgenin varsayılan aracı pasifse (listede aktif olarak yok) plakasıyla, seçilemez biçimde gösterilir.
  function inactiveDefaultOf(vehicle: { id: string; plate: string } | null): boolean {
    if (!vehicle || vehicles.data === undefined) return false;
    return !vehicleItems.some((v) => v.id === vehicle.id);
  }
  const windowSummary = summary ? (days === 7 ? summary.d7 : summary.d30) : null;

  function saved(name: string, rematch: { assigned: number } | null | undefined): void {
    // Kural değişince oranlar ve sipariş dağılımı yeniden hesaplanır; gölge sorguları tazelensin.
    void qc.invalidateQueries({ queryKey: ['shadow-assign'] });
    toast(
      rematch
        ? t('panel.shadowAssign.savedRematch', { name, assigned: rematch.assigned })
        : t('panel.shadowAssign.saved', { name }),
    );
  }

  function changeVehicle(regionId: string, name: string, vehicleId: string): void {
    // Sunucu defaultVehicleId için null kabul etmez; "Araç yok" seçilemez, boş değer istek atmaz.
    if (!vehicleId) return;
    update.mutate(
      { id: regionId, defaultVehicleId: vehicleId },
      {
        onSuccess: (res) => saved(name, res.rematch),
        onError: () => toast(t('panel.shadowAssign.saveError', { name }), 'danger'),
      },
    );
  }

  function changeAuto(regionId: string, name: string, autoAssign: boolean): void {
    update.mutate(
      { id: regionId, autoAssign },
      {
        onSuccess: (res) => saved(name, res.rematch),
        onError: () => toast(t('panel.shadowAssign.saveError', { name }), 'danger'),
      },
    );
  }

  return (
    <section
      data-testid="shadow-assign-card"
      className="card"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
          {t('panel.shadowAssign.title')}
        </h2>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('panel.shadowAssign.subtitle')}
        </p>
      </header>

      {shadow.isError && (
        <p
          role="alert"
          data-testid="shadow-assign-error"
          style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}
        >
          {t('panel.shadowAssign.loadError')}
        </p>
      )}

      {summary && (
        <div
          data-testid="shadow-summary"
          style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          <span data-testid="shadow-rate-7" style={{ fontSize: 14, fontWeight: 600 }}>
            {t('panel.shadowAssign.rateLine', {
              days: 7,
              rate: rateText(summary.d7.ratePct),
              agree: summary.d7.agree,
              comparable: summary.d7.comparable,
            })}
          </span>
          <span data-testid="shadow-rate-30" style={{ fontSize: 14, fontWeight: 600 }}>
            {t('panel.shadowAssign.rateLine', {
              days: 30,
              rate: rateText(summary.d30.ratePct),
              agree: summary.d30.agree,
              comparable: summary.d30.comparable,
            })}
          </span>
          {windowSummary && (
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {t('panel.shadowAssign.sourceHayat')}{' '}
              {rateText(windowSummary.bySource.HAYAT_PANEL.ratePct)}
              {' · '}
              {t('panel.shadowAssign.sourceAbant')}{' '}
              {rateText(windowSummary.bySource.ABANT_PANEL.ratePct)}
            </span>
          )}
        </div>
      )}

      {/* Pencere seçici */}
      <div style={{ display: 'flex', gap: 6 }}>
        {([7, 30] as const).map((n) => (
          <button
            key={n}
            type="button"
            className={days === n ? 'btn btn-primary' : 'btn btn-secondary'}
            style={{ fontSize: 12, padding: '4px 10px' }}
            aria-pressed={days === n}
            data-testid={`shadow-window-${n}`}
            onClick={() => setDays(n)}
          >
            {t(n === 7 ? 'panel.shadowAssign.window7' : 'panel.shadowAssign.window30')}
          </button>
        ))}
      </div>

      {shadow.isPending && (
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      )}

      {/* Bölge → araç kural tablosu */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>
          {t('panel.shadowAssign.rulesTitle')}
        </h3>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('panel.shadowAssign.rulesHint')}
        </p>
        {regionItems.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {t('panel.shadowAssign.noRegions')}
          </p>
        ) : (
          regionItems.map((r) => {
            const match = byRegionId.get(r.id);
            const ruleCell =
              match && match.comparable > 0
                ? t('panel.shadowAssign.ruleMatch', {
                    rate: rateText(match.ratePct),
                    agree: match.agree,
                    comparable: match.comparable,
                  })
                : t('panel.shadowAssign.noRate');
            return (
              <div
                key={r.id}
                data-testid={`shadow-region-${r.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1.2fr auto 1fr',
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: 'var(--bg)',
                  borderRadius: 'var(--r-row)',
                  border: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</span>
                <select
                  aria-label={r.name}
                  value={r.defaultVehicleId ?? ''}
                  disabled={update.isPending}
                  data-testid={`shadow-region-vehicle-${r.id}`}
                  onChange={(e) => changeVehicle(r.id, r.name, e.target.value)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 'var(--r-row)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                  }}
                >
                  <option value="" disabled>
                    {t('panel.shadowAssign.noVehicle')}
                  </option>
                  {inactiveDefaultOf(r.defaultVehicle) && (
                    <option value={r.defaultVehicle!.id} disabled>
                      {t('panel.shadowAssign.inactiveVehicle', { plate: r.defaultVehicle!.plate })}
                    </option>
                  )}
                  {vehicleItems.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.plate}
                      {v.label ? ` · ${v.label}` : ''}
                    </option>
                  ))}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                  <input
                    type="checkbox"
                    checked={r.autoAssign}
                    disabled={update.isPending}
                    data-testid={`shadow-region-auto-${r.id}`}
                    onChange={(e) => changeAuto(r.id, r.name, e.target.checked)}
                  />
                  {t('panel.shadowAssign.autoAssign')}
                </label>
                <span
                  data-testid={`shadow-region-match-${r.id}`}
                  style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}
                >
                  {ruleCell}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Uyuşmayan siparişler */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>
          {t('panel.shadowAssign.mismatchTitle')}
        </h3>
        {data?.rows && mismatches.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {t('panel.shadowAssign.noMismatch')}
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {mismatches.map((row) => (
              <li
                key={row.orderId}
                data-testid={`shadow-mismatch-${row.orderId}`}
                style={{ fontSize: 12.5, display: 'flex', gap: 8, flexWrap: 'wrap' }}
              >
                <span
                  style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}
                >
                  {timeText(row.receivedAt)}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>{sourceLabel(row.source)}</span>
                <span>
                  {t('panel.shadowAssign.panelSide', {
                    label: row.panel.label,
                    who: row.panel.displayName ? ` ${row.panel.displayName}` : '',
                    plate: row.panel.vehicle?.plate ?? t('panel.shadowAssign.notMatched'),
                  })}
                </span>
                <span>
                  {t('panel.shadowAssign.ourSide', {
                    plate: row.ours.vehicle?.plate ?? t('panel.shadowAssign.dash'),
                    via: viaLabel(row.ours.via),
                  })}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {row.ours.region?.name ?? t('panel.shadowAssign.dash')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
